import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Divider,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useParams, useNavigate } from 'react-router-dom';
import { request } from '../../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Edit } from '@mui/icons-material';
import PriceCodeDisplay from '../../../../controlPanel/components/PriceCodeDisplay.tsx';
import { PriceListItem } from '../../../types';
import { usePermissions } from '../../../../../utils/usePermissions';

const PriceListDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { canShowVendor } = usePermissions()

  const navigate = useNavigate();
  const [priceItem, setPriceItem] = useState<PriceListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showVendor, setShowVendor] = useState(false);


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));



  useEffect(() => {
    const fetchPriceItem = async () => {
      try {
        setLoading(true);
        const res = await request({
          url: `priceList/get-one-pricelist/${id}`,
          method: "get",
        });

        if (res.success) {
          setPriceItem(res.data);
        } else {
          setError('Failed to load price list item');
          toast.error('Failed to load price list item');
        }
      } catch (error: any) {
        console.error('Error fetching price item:', error);
        setError(error?.message || 'Error loading price list item');
        toast.error(error?.message || 'Error loading price list item');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPriceItem();
    }
  }, [id]);

  const handleEdit = () => {
    if (priceItem) {
      navigate(`/vendors/priceList/editPriceList/${priceItem._id}`);
    }
  };

  const handleBack = () => {
    navigate('/vendors/priceList');
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? '#00C389' : '#FF4D6D';
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return 'Not set';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to get description text
  const getDescriptionText = (description: any): string => {
    if (!description) return '';

    if (typeof description === 'string') {
      return description;
    }

    if (typeof description === 'object') {
      return description.text || '';
    }

    return '';
  };

  // Helper function to get JSON fields
  const getJsonFields = (description: any): Record<string, any> => {
    if (!description) return {};

    if (typeof description === 'object' && description.jsonFields) {
      return description.jsonFields || {};
    }

    return {};
  };

  // Check if description has content
  const hasDescription = (description: any): boolean => {
    const text = getDescriptionText(description);
    const jsonFields = getJsonFields(description);
    return text.length > 0 || Object.keys(jsonFields).length > 0;
  };


  const handleShowVendor = (e: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    setShowVendor(e.target.checked)
  }

  // Render JSON fields as chips
  const renderJsonFields = (jsonFields: Record<string, any>) => {
    if (!jsonFields || Object.keys(jsonFields).length === 0) {
      return null;
    }



    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          Additional Properties:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Object.entries(jsonFields).map(([key, value]) => (
            <Chip
              key={key}
              label={`${key}: ${value}`}
              size="small"
              variant="outlined"
              sx={{
                fontSize: isMobile ? '0.65rem' : '0.7rem',
                height: isMobile ? 20 : 24,
                maxWidth: isMobile ? '120px' : 'none'
              }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Responsive layout helper for main content sections
  const renderContentSections = () => {
    if (isMobile) {
      return (
        <Stack spacing={1.5}>
          {/* Product Details Card */}
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
              Product Details
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Product Name
                </Typography>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {priceItem!.productName || 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Part Number
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {priceItem!.partNo || 'Not set'}
                </Typography>
              </Box>
              {hasDescription(priceItem!.description) && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Description
                  </Typography>
                  {getDescriptionText(priceItem!.description) && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                      {getDescriptionText(priceItem!.description)}
                    </Typography>
                  )}
                  {renderJsonFields(getJsonFields(priceItem!.description))}
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Vendor & Brand Card */}
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
             {showVendor && `Vendor &`}  Brand
            </Typography>
            <Stack spacing={1}>
              {showVendor && (<Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Vendor Name
                </Typography>

                <Typography variant="body2" fontWeight="medium" noWrap>
                  {priceItem!.vendorName || 'Not set'}
                </Typography>
              </Box>)}

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Product Brand
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {priceItem!.productBrand || 'Not set'}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Vehicle Compatibility Card */}
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
              Vehicle Compatibility
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Car Brand
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {priceItem!.carBrand || 'Not set'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Car Model
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {priceItem!.carModel || 'Not set'}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Pricing Card */}
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
              Pricing Information
            </Typography>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  MRP
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {formatCurrency(priceItem!.mrp)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Purchase Price
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  <PriceCodeDisplay number={priceItem!.purchasePrice ?? 'Not set'} />
                </Typography>
              </Box>
              {priceItem!.mrp && priceItem!.purchasePrice && priceItem!.mrp > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Profit Margin
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {((priceItem!.mrp - priceItem!.purchasePrice) / priceItem!.mrp * 100).toFixed(1)}%
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Stack>
      );
    } else {

      return (
        <Stack spacing={1.5}>
          {/* Main Content Row */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: isTablet ? 'column' : 'row', gap: 3 }}>
              {/* Product Details */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                  Product Details
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Product Name
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {priceItem!.productName || 'Not set'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Part Number
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {priceItem!.partNo || 'Not set'}
                  </Typography>
                </Box>
                {hasDescription(priceItem!.description) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Description
                    </Typography>
                    {getDescriptionText(priceItem!.description) && (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1, fontSize: '0.875rem' }}>
                        {getDescriptionText(priceItem!.description)}
                      </Typography>
                    )}
                    {renderJsonFields(getJsonFields(priceItem!.description))}
                  </Box>
                )}
              </Box>

              {/* Vendor & Brand */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                 {showVendor && `Vendor &`}  Brand
                </Typography>
                {showVendor && (<Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vendor Name
                  </Typography>

                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {priceItem!.vendorName || 'Not set'}
                  </Typography>
                </Box>)}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Product Brand
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {priceItem!.productBrand || 'Not set'}
                  </Typography>
                </Box>
              </Box>

              {/* Vehicle Compatibility */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                  Vehicle Compatibility
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Car Brand
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {priceItem!.carBrand || 'Not set'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Car Model
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {priceItem!.carModel || 'Not set'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Pricing Row */}
          <Paper elevation={1} sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
              Pricing Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  MRP
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {formatCurrency(priceItem!.mrp)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Purchase Price
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  <PriceCodeDisplay number={priceItem!.purchasePrice ?? 'Not set'} />
                </Typography>
              </Box>
              {priceItem!.mrp && priceItem!.purchasePrice && priceItem!.mrp > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Profit Margin
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {((priceItem!.mrp - priceItem!.purchasePrice) / priceItem!.mrp * 100).toFixed(1)}%
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Stack>
      );
    }
  };

  if (loading) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Brandcrumb />
        </Box>
        <Card elevation={4}>
          <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </CardContent>
        </Card>
      </>
    );
  }

  if (error || !priceItem) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Brandcrumb />
        </Box>
        <Card elevation={4}>
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || 'Price list item not found'}
            </Alert>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={handleBack}
              size={isMobile ? 'small' : 'medium'}
            >
              Back to Price List
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Brandcrumb />
          <Chip
            onClick={handleBack}
            icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
            label="Back"
            variant="filled"
            color="secondary"
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          />
        </Box>
      </Box>

      <Card elevation={4}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          {/* Compact Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <RequestQuoteIcon color="primary" sx={{ fontSize: isMobile ? 24 : 28, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight="bold"
                  noWrap
                  sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                >
                  {priceItem.productName || 'Unnamed Product'}
                </Typography>
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {canShowVendor() && <FormControlLabel control={<Switch onChange={handleShowVendor} />} label="Show Vendor" />}
                  <Button
                    variant="contained"
                    size='small'
                    onClick={() => navigate(`/inventory/addProduct`, {
                      state: {
                        source: 'price-list',
                        pricelistItemId: priceItem._id
                      }
                    })}
                    sx={{
                      fontSize: isMobile ? '0.6rem' : '0.60rem',
                      py: isMobile ? 0.2 : 0.1,
                      px: isMobile ? 1.5 : 2,
                      minWidth: isMobile ? 100 : 'auto'
                    }}
                  >
                    {isMobile ? 'To Inventory' : 'Move to Inventory'}
                  </Button>
                  <Tooltip title="Edit Product">
                    <IconButton
                      size="small"
                      onClick={handleEdit}
                      color="primary"
                    >
                      <Edit fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                  Part No: {priceItem.partNo || 'Not set'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: 10,
                      color: getStatusColor(priceItem.status)
                    }}
                  />
                  <Chip
                    label={priceItem.status}
                    size="small"
                    color={priceItem.status === 'active' ? 'success' : 'error'}
                    sx={{
                      height: isMobile ? 20 : 22,
                      fontSize: isMobile ? '0.6rem' : '0.65rem'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Responsive Content Sections */}
          {renderContentSections()}

          {/* Metadata - Compact */}
          <Paper elevation={1} sx={{ p: 1, mt: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created
                  </Typography>
                  <Typography variant="caption" fontWeight="medium">
                    {formatDate(priceItem.createdAt)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last Updated
                  </Typography>
                  <Typography variant="caption" fontWeight="medium">
                    {formatDate(priceItem.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </CardContent>
      </Card>
    </>
  );
};

export default PriceListDetail;